import { IPromise } from 'angular';
import { StripeInvoiceSummary } from '../../app-common/services/subscription.models';

export const AiCreditsPurchaseServiceName = 'gpAiCreditsPurchaseService';

/** A purchasable credit pack shown on the account page. */
export interface AiCreditPack {
    /** Stable id for the pack (passed back on purchase). */
    id: string;
    /** Price in whole USD dollars. */
    priceUsd: number;
    /** Credits the buyer receives (includes any bonus). */
    credits: number;
    /** Optional badge, e.g. 'Most popular' or '+10%'. */
    badge?: string;
    /** Highlight this pack as the recommended/anchor option. */
    featured?: boolean;
}

/**
 * One credit pool the buyer can top up (#724): their own personal pool, or a team
 * they are an active member of.
 *
 * Deliberately thinner than the `AiCreditPool` of ai-credits-contract.ts — choosing a
 * pool needs its name and whether it may be bought for, not what is in it, and the
 * balances panel already shows the figures.
 */
export interface AiCreditPurchasePool {
    /**
     * Whether this pool belongs to the user themselves or to a team. The wording the
     * rest of the credits API uses, and what {@link IAiCreditsPurchaseApi.startCheckout}
     * sends back.
     */
    ownerType: 'user' | 'team';
    /** Identity user id, or team id (a GUID string). Stable key for the row. */
    ownerId: string;
    /**
     * The team's name. Explicitly null for the personal pool — the client picks its
     * own wording for that one.
     */
    name: string | null;
    /**
     * True on the one pool the user's AI usage is currently billed to. Exactly one
     * pool in the array has this set, and it is the pool a purchase tops up when the
     * buyer chooses nothing.
     */
    isCurrent: boolean;
    /**
     * Whether this user may buy top-up credits for THIS pool: always true for their
     * personal pool, team-owner/billing-manager only for a team pool (#76, #465).
     * Purchase rights are per pool, so a user can be able to top up one of their
     * teams and not another. Enforced server-side too — this only decides what to
     * offer.
     */
    canPurchase: boolean;
}

/** The catalogue plus whether purchasing is live yet. */
export interface AiCreditPurchaseOptions {
    /** Packs to offer, in display order. */
    packs: AiCreditPack[];
    /** False until the deployment's pack catalogue is configured; the UI shows "coming soon". */
    available: boolean;
    /**
     * Whether this user may buy for the pool they are BILLED to: always true for a
     * personal pool, team-owner/billing-manager only for a team pool. Enforced
     * server-side too. Since #724 this is the {@link pools} row with `isCurrent`, kept
     * as its own field because it is what the panel meant before pools were selectable.
     */
    canPurchase: boolean;
    /**
     * Every pool this user could buy credits for (#724) — personal first, then each
     * team they are an active member of, in join order. Never empty, and exactly one
     * row has {@link AiCreditPurchasePool.isCurrent}. Pools the user may NOT buy for
     * are included: filtering is the panel's decision, not the wire's.
     */
    pools: AiCreditPurchasePool[];
}

/** A card the user already has saved with us (#473). Never carries a PaymentMethod id. */
export interface AiCreditSavedCard {
    /** Stripe's lower-case brand token, e.g. 'visa'. */
    brand?: string;
    last4?: string;
    expMonth?: number;
    expYear?: number;
}

/** What the account page needs to decide whether to offer the saved card. */
export interface AiCreditSavedCards {
    cards: AiCreditSavedCard[];
    /**
     * True when at least one saved card would not be offered by Stripe Checkout as
     * things stand, so the buyer should be asked before we make it available.
     */
    consentNeeded: boolean;
}

/** Result of confirming a checkout after the payer returns from Stripe. */
export interface AiCreditPurchaseConfirmation {
    /** True when the credits are in the ledger (posted now, or already by another path). */
    credited: boolean;
    /** Backend outcome name, for diagnostics. */
    outcome: string;
}

/**
 * Seam for the credit-purchase UI (#76). The real service talks to the web role's
 * Stripe-backed endpoints; the mock returns a static catalogue in the "coming soon"
 * state. Both implement the same shape, so swapping the registration in main.ts is
 * the only change required — the directive and template stay untouched. Same
 * pattern as {@link IAiCreditsApi}.
 */
export interface IAiCreditsPurchaseApi {
    getOptions(): IPromise<AiCreditPurchaseOptions>;
    /** The cards already saved with us, and whether Checkout needs consent to offer them (#473). */
    getSavedCards(): IPromise<AiCreditSavedCards>;
    /**
     * Start a Stripe hosted checkout for a pack; resolves to the URL to redirect to.
     * Pass consentToSavedCards when the buyer agreed to reuse a card already on file —
     * their cards are made redisplayable before the session is created.
     *
     * `pool` is the pool to top up (#724). Omit it — or pass null — for the pool the
     * user is currently billed to, which is what this call meant before pools were
     * selectable. The server re-checks whichever pool is named against the user's own
     * catalogue and their purchase rights, so this is a choice, never an authority.
     */
    startCheckout(
        packId: string,
        consentToSavedCards?: boolean,
        pool?: AiCreditPurchasePool | null,
    ): IPromise<string>;
    /** Verify a completed checkout (by session id from the success redirect) and post the credits. */
    confirmCheckout(sessionId: string): IPromise<AiCreditPurchaseConfirmation>;
    /** Past credit-purchase invoices for the current user (Stripe-hosted; #159). */
    getInvoices(): IPromise<StripeInvoiceSummary[]>;
}
